import { UserRole } from '@/shared/enums';

export interface FieldConfig {
  id: string;
  label: string;
  type: 'text' | 'email' | 'tel' | 'number' | 'select' | 'date' | 'textarea' | 'search' | 'file';
  required: boolean;
  visible: boolean;
  priority: 'high' | 'medium' | 'low';
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    custom?: string;
  };
  helpText?: string;
  icon?: string;
  dependsOn?: string[];
  showWhen?: (values: any) => boolean;
}

export interface FormConfig {
  title: string;
  description: string;
  icon: string;
  steps: {
    id: string;
    title: string;
    description: string;
    icon: string;
    fields: string[];
    required: boolean;
  }[];
  fields: Record<string, FieldConfig>;
  quickActions: Array<{
    label: string;
    action: string;
    icon: string;
  }>;
  validation: {
    strict: boolean;
    showWarnings: boolean;
    contextualValidation: boolean;
  };
}

export const formConfigs: Record<UserRole, FormConfig> = {
  VENDEDOR: {
    title: "Novo Contrato - Vendedor",
    description: "Cadastro rápido e intuitivo para vendedores",
    icon: "User",
    steps: [
      {
        id: "cliente",
        title: "Cliente",
        description: "Selecione ou cadastre o cliente",
        icon: "User",
        fields: ["cliente_search", "cliente_id"],
        required: true
      },
      {
        id: "produto",
        title: "Produto",
        description: "Escolha o grupo e bem",
        icon: "Package",
        fields: ["grupo_id", "bem_id", "valorParcela", "contrato_numero"],
        required: true
      },
      {
        id: "financeiro",
        title: "Valores",
        description: "Configure valores e pagamento",
        icon: "DollarSign",
        fields: ["metodoPagamento"],
        required: true
      }
    ],
    fields: {
      cliente_search: {
        id: "cliente_search",
        label: "Buscar Cliente",
        type: "search",
        required: false, // Campo de busca não deve ser obrigatório para validação
        visible: true,
        priority: "high",
        placeholder: "Digite CPF, nome ou email",
        helpText: "Busque por CPF, nome completo ou email",
        icon: "User"
      },
      cliente_id: {
        id: "cliente_id",
        label: "Cliente",
        type: "select",
        required: true,
        visible: false, // Hidden, populated by search
        priority: "high"
      },
      grupo_id: {
        id: "grupo_id",
        label: "Grupo",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Selecione o grupo",
        helpText: "Grupos sugeridos baseados no perfil do cliente",
        icon: "Package"
      },
      bem_id: {
        id: "bem_id",
        label: "Bem",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Selecione o bem",
        helpText: "Bens sugeridos baseados no valor da parcela",
        icon: "Package"
      },
      valorParcela: {
        id: "valorParcela",
        label: "Valor da Parcela",
        type: "text",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "R$ 0,00",
        validation: {
          pattern: "^R\\$ [0-9]{1,3}(\\.[0-9]{3})*(,[0-9]{2})?$",
          custom: "currency_validation"
        },
        helpText: "Valor obtido do bem ou preenchido manualmente",
        icon: "DollarSign"
      },
      contrato_numero: {
        id: "contrato_numero",
        label: "Número do Contrato",
        type: "text",
        required: false,
        visible: true,
        priority: "medium",
        placeholder: "Ex: CONT_2024_001",
        helpText: "Número único do contrato (gerado automaticamente se vazio)",
        icon: "FileText"
      },
      metodoPagamento: {
        id: "metodoPagamento",
        label: "Método de Pagamento",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        options: [
          { value: "PIX", label: "PIX" },
          { value: "BOLETO", label: "Boleto" },
          { value: "CARTAO", label: "Cartão de Crédito" }
        ]
      },
      cota: {
        id: "cota",
        label: "Número da Cota",
        type: "text",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "Ex: 001",
        helpText: "Número da cota atribuído a este contrato",
        icon: "Hash"
      }
    },
    quickActions: [
      {
        label: "Salvar Rascunho",
        action: "save_draft",
        icon: "FileText"
      },
    ],
    validation: {
      strict: false,
      showWarnings: true,
      contextualValidation: true
    }
  },

  SECRETARIA: {
    title: "Novo Contrato - Secretaria",
    description: "Formulário completo para revisão e aprovação",
    icon: "FileText",
    steps: [
      {
        id: "cliente",
        title: "Cliente",
        description: "Dados completos do cliente",
        icon: "User",
        fields: ["cliente_search", "cliente_id", "cliente_verificacao"],
        required: true
      },
      {
        id: "produto",
        title: "Produto",
        description: "Grupo, bem e valores detalhados",
        icon: "Package",
        fields: ["grupo_id", "bem_id", "valorParcela", "taxa_adm", "valor_total", "valor_entrada", "contrato_numero"],
        required: true
      },
      {
        id: "financeiro",
        title: "Financeiro",
        description: "Valores, comissões e pagamentos",
        icon: "DollarSign",
        fields: ["metodoPagamento", "consultor_id", "comissao_vendedor", "comissao_consultor"],
        required: true
      },
      {
        id: "documentos",
        title: "Documentos",
        description: "Documentação e aprovações",
        icon: "FileText",
        fields: ["documentos_obrigatorios", "observacoes", "aprovacao_previa"],
        required: true
      }
    ],
    fields: {
      cliente_search: {
        id: "cliente_search",
        label: "Buscar Cliente",
        type: "search",
        required: false, // Campo de busca não deve ser obrigatório para validação
        visible: true,
        priority: "high",
        placeholder: "Digite CPF, nome ou email",
        helpText: "Busque por CPF, nome completo ou email",
        icon: "User"
      },
      cliente_id: {
        id: "cliente_id",
        label: "Cliente",
        type: "select",
        required: true,
        visible: false,
        priority: "high"
      },
      cliente_verificacao: {
        id: "cliente_verificacao",
        label: "Verificação de Cliente",
        type: "textarea",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "Observações sobre o cliente",
        helpText: "Verifique se o cliente está apto para o contrato",
        icon: "CheckCircle"
      },
      grupo_id: {
        id: "grupo_id",
        label: "Grupo",
        type: "select",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "Selecione o grupo",
        helpText: "Verifique se o grupo é adequado",
        icon: "Package"
      },
      bem_id: {
        id: "bem_id",
        label: "Bem",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Selecione o bem",
        helpText: "Verifique se o bem está disponível",
        icon: "Package"
      },
      consultor_id: {
        id: "consultor_id",
        label: "Consultor Responsável",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Selecione o consultor",
        helpText: "Selecione o consultor lider responsável pela venda",
        icon: "User"
      },
      valorParcela: {
        id: "valorParcela",
        label: "Valor da Parcela",
        type: "text",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "R$ 0,00",
        validation: {
          pattern: "^R\\$ [0-9]{1,3}(\\.[0-9]{3})*(,[0-9]{2})?$",
          custom: "currency_validation"
        },
        helpText: "Valor obtido do bem ou preenchido manualmente",
        icon: "DollarSign"
      },
      taxa_adm: {
        id: "taxa_adm",
        label: "Taxa de Administração",
        type: "number",
        required: false,
        visible: true,
        priority: "high",
        icon: "DollarSign",
        validation: {
          custom: "readonly"
        }
      },
      valor_total: {
        id: "valor_total",
        label: "Valor Total",
        type: "number",
        required: false,
        visible: true,
        priority: "medium",
        icon: "DollarSign",
        validation: {
          custom: "readonly"
        }
      },
      valor_entrada: {
        id: "valor_entrada",
        label: "Valor da Entrada",
        type: "text",
        required: false,
        visible: true,
        priority: "medium",
        placeholder: "R$ 0,00",
        validation: {
          pattern: "^R\\$ [0-9]{1,3}(\\.[0-9]{3})*(,[0-9]{2})?$",
          custom: "currency_validation"
        },
        helpText: "Valor da entrada inicial do contrato",
        icon: "DollarSign"
      },
      metodoPagamento: {
        id: "metodoPagamento",
        label: "Método de Pagamento",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        options: [
          { value: "PIX", label: "PIX" },
          { value: "BOLETO", label: "Boleto" },
          { value: "CARTAO", label: "Cartão de Crédito" }
        ]
      },
      comissao_vendedor: {
        id: "comissao_vendedor",
        label: "Comissão do Vendedor",
        type: "number",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "0,00",
        helpText: "Comissão calculada automaticamente",
        icon: "DollarSign",
        validation: {
          custom: "readonly"
        }
      },
      comissao_consultor: {
        id: "comissao_consultor",
        label: "Comissão do Consultor",
        type: "number",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "0,00",
        helpText: "Comissão calculada automaticamente",
        icon: "DollarSign",
        validation: {
          custom: "readonly"
        }
      },
      documentos_obrigatorios: {
        id: "documentos_obrigatorios",
        label: "Documentos Obrigatórios",
        type: "file",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "Selecione os arquivos",
        helpText: "Faça upload dos documentos obrigatórios",
        icon: "FileText"
      },
      observacoes: {
        id: "observacoes",
        label: "Observações",
        type: "textarea",
        required: false,
        visible: true,
        priority: "low",
        placeholder: "Observações adicionais",
        helpText: "Observações sobre o contrato",
        icon: "Info"
      },
      aprovacao_previa: {
        id: "aprovacao_previa",
        label: "Aprovação Prévia",
        type: "select",
        required: false,
        visible: true,
        priority: "high",
        options: [
          { value: "APROVADO", label: "Aprovado" },
          { value: "PENDENTE", label: "Pendente" },
          { value: "REJEITADO", label: "Rejeitado" }
        ],
        helpText: "Status de aprovação prévia",
        icon: "CheckCircle"
      }
    },
    quickActions: [
      {
        label: "Salvar Rascunho",
        action: "save_draft",
        icon: "FileText"
      }
    ],
    validation: {
      strict: true,
      showWarnings: true,
      contextualValidation: true
    }
  },

  ADMIN: {
    title: "Novo Contrato - Administrador",
    description: "Acesso completo a todas as funcionalidades",
    icon: "Settings",
    steps: [
      {
        id: "cliente",
        title: "Cliente",
        description: "Dados completos do cliente",
        icon: "User",
        fields: ["cliente_search", "cliente_id"],
        required: false
      },
      {
        id: "produto",
        title: "Produto",
        description: "Grupo, bem e valores detalhados",
        icon: "Package",
        fields: ["grupo_id", "bem_id", "valorParcela", "meses_restantes", "taxa_adm", "valor_total", "valor_entrada", "contrato_numero"],
        required: true
      },
      {
        id: "financeiro",
        title: "Financeiro",
        description: "Valores, comissões e pagamentos",
        icon: "DollarSign",
        fields: ["metodoPagamento", "consultor_id", "comissao_vendedor", "comissao_consultor", "observacoes"],
        required: true
      }
    ],
    fields: {
      // Campos da SECRETARIA (copiados para evitar referência circular)
      cliente_search: {
        id: "cliente_search",
        label: "Buscar Cliente",
        type: "search",
        required: false, // Campo de busca não deve ser obrigatório para validação
        visible: true,
        priority: "high",
        placeholder: "Digite CPF, nome ou email",
        helpText: "Busque por CPF, nome completo ou email",
        icon: "User"
      },
      cliente_id: {
        id: "cliente_id",
        label: "Cliente",
        type: "select",
        required: true,
        visible: false,
        priority: "high"
      },
      cliente_verificacao: {
        id: "cliente_verificacao",
        label: "Verificação de Cliente",
        type: "textarea",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "Observações sobre o cliente",
        helpText: "Verifique se o cliente está apto para o contrato",
        icon: "CheckCircle"
      },
      grupo_id: {
        id: "grupo_id",
        label: "Grupo",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Selecione o grupo",
        helpText: "Verifique se o grupo é adequado",
        icon: "Package"
      },
      bem_id: {
        id: "bem_id",
        label: "Bem",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Selecione o bem",
        helpText: "Verifique se o bem está disponível",
        icon: "Package"
      },
      valorParcela: {
        id: "valorParcela",
        label: "Valor da Parcela",
        type: "text",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "R$ 0,00",
        validation: {
          pattern: "^R\\$ [0-9]{1,3}(\\.[0-9]{3})*(,[0-9]{2})?$",
          custom: "currency_validation"
        },
        helpText: "Valor obtido do bem ou preenchido manualmente",
        icon: "DollarSign"
      },
      meses_restantes: {
        id: "meses_restantes",
        label: "Meses Restantes",
        type: "number",
        required: false,
        visible: true,
        priority: "medium",
        placeholder: "Ex: 150",
        helpText: "Ajuste a quantidade de meses restantes do grupo",
        icon: "Calendar"
      },
      taxa_adm: {
        id: "taxa_adm",
        label: "Taxa de Administração",
        type: "number",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "0,00",
        helpText: "Taxa de administração do grupo (calculada automaticamente)",
        icon: "DollarSign",
        validation: {
          custom: "readonly"
        }
      },
      valor_total: {
        id: "valor_total",
        label: "Valor Total",
        type: "number",
        required: false,
        visible: true,
        priority: "medium",
        placeholder: "0,00",
        helpText: "Valor total calculado automaticamente",
        icon: "DollarSign",
        validation: {
          custom: "readonly"
        }
      },
      valor_entrada: {
        id: "valor_entrada",
        label: "Valor da Entrada",
        type: "text",
        required: false,
        visible: true,
        priority: "medium",
        placeholder: "R$ 0,00",
        validation: {
          pattern: "^R\\$ [0-9]{1,3}(\\.[0-9]{3})*(,[0-9]{2})?$",
          custom: "currency_validation"
        },
        helpText: "Valor da entrada inicial do contrato",
        icon: "DollarSign"
      },
      metodoPagamento: {
        id: "metodoPagamento",
        label: "Método de Pagamento",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        options: [
          { value: "PIX", label: "PIX" },
          { value: "BOLETO", label: "Boleto" },
          { value: "CARTAO", label: "Cartão de Crédito" }
        ]
      },
      comissao_vendedor: {
        id: "comissao_vendedor",
        label: "Comissão do Vendedor",
        type: "number",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "0,00",
        helpText: "Comissão calculada automaticamente",
        icon: "DollarSign",
        validation: {
          custom: "readonly"
        }
      },
      comissao_consultor: {
        id: "comissao_consultor",
        label: "Comissão do Consultor",
        type: "number",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "0,00",
        helpText: "Comissão calculada automaticamente",
        icon: "DollarSign",
        validation: {
          custom: "readonly"
        }
      },
      documentos_obrigatorios: {
        id: "documentos_obrigatorios",
        label: "Documentos Obrigatórios",
        type: "file",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Selecione os arquivos",
        helpText: "Faça upload dos documentos obrigatórios",
        icon: "FileText"
      },
      observacoes: {
        id: "observacoes",
        label: "Observações",
        type: "textarea",
        required: false,
        visible: true,
        priority: "low",
        placeholder: "Observações adicionais",
        helpText: "Observações sobre o contrato",
        icon: "Info"
      },
      aprovacao_previa: {
        id: "aprovacao_previa",
        label: "Aprovação Prévia",
        type: "select",
        required: false,
        visible: true,
        priority: "high",
        options: [
          { value: "APROVADO", label: "Aprovado" },
          { value: "PENDENTE", label: "Pendente" },
          { value: "REJEITADO", label: "Rejeitado" }
        ],
        helpText: "Status de aprovação prévia",
        icon: "CheckCircle"
      },
      configuracoes_avancadas: {
        id: "configuracoes_avancadas",
        label: "Configurações Avançadas",
        type: "textarea",
        required: false,
        visible: true,
        priority: "low",
        placeholder: "Configurações especiais",
        helpText: "Configurações avançadas do contrato",
        icon: "Settings"
      },
      contrato_numero: {
        id: "contrato_numero",
        label: "Número do Contrato",
        type: "text",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Ex: CONT_2024_001",
        helpText: "Código único do contrato",
        icon: "FileText"
      }
    },
    quickActions: [
      {
        label: "Salvar Rascunho",
        action: "save_draft",
        icon: "FileText"
      }
    ],
    validation: {
      strict: false,
      showWarnings: true,
      contextualValidation: true
    }
  },

  SUPERVISOR: {
    title: "Novo Contrato - Supervisor",
    description: "Supervisão e gestão de contratos",
    icon: "CheckCircle",
    steps: [
      {
        id: "cliente",
        title: "Cliente",
        description: "Dados completos do cliente",
        icon: "User",
        fields: ["cliente_search", "cliente_id"],
        required: true
      },
      {
        id: "produto",
        title: "Produto",
        description: "Grupo, bem e valores detalhados",
        icon: "Package",
        fields: ["grupo_id", "bem_id", "valorParcela", "taxa_adm", "valor_total", "valor_entrada", "contrato_numero"],
        required: true
      },
      {
        id: "financeiro",
        title: "Valores",
        description: "Configure valores e pagamento",
        icon: "DollarSign",
        fields: ["metodoPagamento", "consultor_id"],
        required: true
      }
    ],
    fields: {
      cliente_search: {
        id: "cliente_search",
        label: "Buscar Cliente",
        type: "search",
        required: false, // Campo de busca não deve ser obrigatório para validação
        visible: true,
        priority: "high",
        placeholder: "Digite CPF, nome ou email",
        helpText: "Busque por CPF, nome completo ou email",
        icon: "User"
      },
      cliente_id: {
        id: "cliente_id",
        label: "Cliente",
        type: "select",
        required: true,
        visible: false,
        priority: "high"
      },
      cliente_verificacao: {
        id: "cliente_verificacao",
        label: "Verificação de Cliente",
        type: "textarea",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "Observações sobre o cliente",
        helpText: "Verifique se o cliente está apto para o contrato",
        icon: "CheckCircle"
      },
      grupo_id: {
        id: "grupo_id",
        label: "Grupo",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Selecione o grupo",
        helpText: "Verifique se o grupo é adequado",
        icon: "Package"
      },
      bem_id: {
        id: "bem_id",
        label: "Bem",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Selecione o bem",
        helpText: "Verifique se o bem está disponível",
        icon: "Package"
      },
      valorParcela: {
        id: "valorParcela",
        label: "Valor da Parcela",
        type: "text",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "R$ 0,00",
        validation: {
          pattern: "^R\\$ [0-9]{1,3}(\\.[0-9]{3})*(,[0-9]{2})?$",
          custom: "currency_validation"
        },
        helpText: "Valor obtido do bem ou preenchido manualmente",
        icon: "DollarSign"
      },
      taxa_adm: {
        id: "taxa_adm",
        label: "Taxa de Administração",
        type: "number",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "0,00",
        helpText: "Taxa de administração do grupo (calculada automaticamente)",
        icon: "DollarSign",
        validation: {
          custom: "readonly"
        }
      },
      valor_total: {
        id: "valor_total",
        label: "Valor Total",
        type: "number",
        required: false,
        visible: true,
        priority: "medium",
        placeholder: "0,00",
        helpText: "Valor total calculado automaticamente",
        icon: "DollarSign",
        validation: {
          custom: "readonly"
        }
      },
      valor_entrada: {
        id: "valor_entrada",
        label: "Valor da Entrada",
        type: "text",
        required: false,
        visible: true,
        priority: "medium",
        placeholder: "R$ 0,00",
        validation: {
          pattern: "^R\\$ [0-9]{1,3}(\\.[0-9]{3})*(,[0-9]{2})?$",
          custom: "currency_validation"
        },
        helpText: "Valor da entrada inicial do contrato",
        icon: "DollarSign"
      },
      metodoPagamento: {
        id: "metodoPagamento",
        label: "Método de Pagamento",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        options: [
          { value: "PIX", label: "PIX" },
          { value: "BOLETO", label: "Boleto" },
          { value: "CARTAO", label: "Cartão de Crédito" }
        ]
      },
      comissao_vendedor: {
        id: "comissao_vendedor",
        label: "Comissão do Vendedor",
        type: "number",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "0,00",
        helpText: "Comissão calculada automaticamente",
        icon: "DollarSign",
        validation: {
          custom: "readonly"
        }
      },
      comissao_consultor: {
        id: "comissao_consultor",
        label: "Comissão do Consultor",
        type: "number",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "0,00",
        helpText: "Comissão calculada automaticamente",
        icon: "DollarSign",
        validation: {
          custom: "readonly"
        }
      },
      configuracoes_avancadas: {
        id: "configuracoes_avancadas",
        label: "Configurações Avançadas",
        type: "textarea",
        required: false,
        visible: true,
        priority: "low",
        placeholder: "Configurações especiais",
        helpText: "Configurações avançadas do contrato",
        icon: "Settings"
      },
      contrato_numero: {
        id: "contrato_numero",
        label: "Número do Contrato",
        type: "text",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "Ex: CONT_2024_001",
        helpText: "Código único do contrato",
        icon: "FileText"
      },
      observacoes: {
        id: "observacoes",
        label: "Observações",
        type: "textarea",
        required: false,
        visible: true,
        priority: "low",
        placeholder: "Observações adicionais",
        helpText: "Observações sobre o contrato",
        icon: "Info"
      },
      aprovacao_previa: {
        id: "aprovacao_previa",
        label: "Aprovação Prévia",
        type: "select",
        required: false,
        visible: true,
        priority: "high",
        options: [
          { value: "APROVADO", label: "Aprovado" },
          { value: "PENDENTE", label: "Pendente" },
          { value: "REJEITADO", label: "Rejeitado" }
        ],
        helpText: "Status de aprovação prévia",
        icon: "CheckCircle"
      }
    },
    quickActions: [
      {
        label: "Salvar Rascunho",
        action: "save_draft",
        icon: "FileText"
      }
    ],
    validation: {
      strict: false,
      showWarnings: true,
      contextualValidation: true
    }
  },

  SUPER_ADMIN: {
    title: "Novo Contrato - Super Admin",
    description: "Acesso total ao sistema",
    icon: "Settings",
    steps: [
      {
        id: "cliente",
        title: "Cliente",
        description: "Dados completos do cliente",
        icon: "User",
        fields: ["cliente_search", "cliente_id"],
        required: true
      },
      {
        id: "produto",
        title: "Produto",
        description: "Grupo, bem e valores detalhados",
        icon: "Package",
        fields: ["grupo_id", "bem_id", "valorParcela", "taxa_adm", "valor_total", "valor_entrada", "contrato_numero"],
        required: true
      },
      {
        id: "financeiro",
        title: "Financeiro",
        description: "Valores, comissões e pagamentos",
        icon: "DollarSign",
        fields: ["metodoPagamento", "consultor_id", "comissao_vendedor", "comissao_consultor", "observacoes"],
        required: true
      }
    ],
    fields: {
      cliente_search: {
        id: "cliente_search",
        label: "Buscar Cliente",
        type: "search",
        required: false, // Campo de busca não deve ser obrigatório para validação
        visible: true,
        priority: "high",
        placeholder: "Digite CPF, nome ou email",
        helpText: "Busque por CPF, nome completo ou email",
        icon: "User"
      },
      cliente_id: {
        id: "cliente_id",
        label: "Cliente",
        type: "select",
        required: true,
        visible: false,
        priority: "high"
      },
      cliente_verificacao: {
        id: "cliente_verificacao",
        label: "Verificação de Cliente",
        type: "textarea",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "Observações sobre o cliente",
        helpText: "Verifique se o cliente está apto para o contrato",
        icon: "CheckCircle"
      },
      grupo_id: {
        id: "grupo_id",
        label: "Grupo",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Selecione o grupo",
        helpText: "Verifique se o grupo é adequado",
        icon: "Package"
      },
      bem_id: {
        id: "bem_id",
        label: "Bem",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Selecione o bem",
        helpText: "Verifique se o bem está disponível",
        icon: "Package"
      },
      valorParcela: {
        id: "valorParcela",
        label: "Valor da Parcela",
        type: "text",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "R$ 0,00",
        validation: {
          pattern: "^R\\$ [0-9]{1,3}(\\.[0-9]{3})*(,[0-9]{2})?$",
          custom: "currency_validation"
        },
        helpText: "Valor obtido do bem ou preenchido manualmente",
        icon: "DollarSign"
      },
      taxa_adm: {
        id: "taxa_adm",
        label: "Taxa de Administração",
        type: "number",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "0,00",
        helpText: "Taxa de administração do grupo (calculada automaticamente)",
        icon: "DollarSign",
        validation: {
          custom: "readonly"
        }
      },
      valor_total: {
        id: "valor_total",
        label: "Valor Total",
        type: "number",
        required: false,
        visible: true,
        priority: "medium",
        placeholder: "0,00",
        helpText: "Valor total calculado automaticamente",
        icon: "DollarSign",
        validation: {
          custom: "readonly"
        }
      },
      valor_entrada: {
        id: "valor_entrada",
        label: "Valor da Entrada",
        type: "text",
        required: false,
        visible: true,
        priority: "medium",
        placeholder: "R$ 0,00",
        validation: {
          pattern: "^R\\$ [0-9]{1,3}(\\.[0-9]{3})*(,[0-9]{2})?$",
          custom: "currency_validation"
        },
        helpText: "Valor da entrada inicial do contrato",
        icon: "DollarSign"
      },
      metodoPagamento: {
        id: "metodoPagamento",
        label: "Método de Pagamento",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        options: [
          { value: "PIX", label: "PIX" },
          { value: "BOLETO", label: "Boleto" },
          { value: "CARTAO", label: "Cartão de Crédito" }
        ]
      },
      comissao_vendedor: {
        id: "comissao_vendedor",
        label: "Comissão do Vendedor",
        type: "number",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "0,00",
        helpText: "Comissão calculada automaticamente",
        icon: "DollarSign",
        validation: {
          custom: "readonly"
        }
      },
      comissao_consultor: {
        id: "comissao_consultor",
        label: "Comissão do Consultor",
        type: "number",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "0,00",
        helpText: "Comissão calculada automaticamente",
        icon: "DollarSign",
        validation: {
          custom: "readonly"
        }
      },
      configuracoes_avancadas: {
        id: "configuracoes_avancadas",
        label: "Configurações Avançadas",
        type: "textarea",
        required: false,
        visible: true,
        priority: "low",
        placeholder: "Configurações especiais",
        helpText: "Configurações avançadas do contrato",
        icon: "Settings"
      },
      contrato_numero: {
        id: "contrato_numero",
        label: "Número do Contrato",
        type: "text",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Ex: CONT_2024_001",
        helpText: "Código único do contrato",
        icon: "FileText"
      },
      observacoes: {
        id: "observacoes",
        label: "Observações",
        type: "textarea",
        required: false,
        visible: true,
        priority: "low",
        placeholder: "Observações adicionais",
        helpText: "Observações sobre o contrato",
        icon: "Info"
      },
      aprovacao_previa: {
        id: "aprovacao_previa",
        label: "Aprovação Prévia",
        type: "select",
        required: false,
        visible: true,
        priority: "high",
        options: [
          { value: "APROVADO", label: "Aprovado" },
          { value: "PENDENTE", label: "Pendente" },
          { value: "REJEITADO", label: "Rejeitado" }
        ],
        helpText: "Status de aprovação prévia",
        icon: "CheckCircle"
      }
    },
    quickActions: [
      {
        label: "Salvar Rascunho",
        action: "save_draft",
        icon: "FileText"
      },
      {
        label: "Configurações",
        action: "settings",
        icon: "Settings"
      }
    ],
    validation: {
      strict: false,
      showWarnings: true,
      contextualValidation: true
    }
  },

  RELACIONADOR: {
    title: "Novo Contrato - Relacionador",
    description: "Cadastro rápido e intuitivo para relacionadores",
    icon: "Users",
    steps: [
      {
        id: "cliente",
        title: "Cliente",
        description: "Selecione ou cadastre o cliente",
        icon: "User",
        fields: ["cliente_search", "cliente_id"],
        required: true
      },
      {
        id: "produto",
        title: "Produto",
        description: "Escolha o grupo e bem",
        icon: "Package",
        fields: ["grupo_id", "bem_id", "valorParcela", "contrato_numero"],
        required: true
      },
      {
        id: "financeiro",
        title: "Valores",
        description: "Configure valores e pagamento",
        icon: "DollarSign",
        fields: ["metodoPagamento"],
        required: true
      }
    ],
    fields: {
      cliente_search: {
        id: "cliente_search",
        label: "Buscar Cliente",
        type: "search",
        required: false, // Campo de busca não deve ser obrigatório para validação
        visible: true,
        priority: "high",
        placeholder: "Digite CPF, nome ou email",
        helpText: "Busque por CPF, nome completo ou email",
        icon: "User"
      },
      cliente_id: {
        id: "cliente_id",
        label: "Cliente",
        type: "select",
        required: true,
        visible: false, // Hidden, populated by search
        priority: "high"
      },
      grupo_id: {
        id: "grupo_id",
        label: "Grupo",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Selecione o grupo",
        helpText: "Grupos sugeridos baseados no perfil do cliente",
        icon: "Package"
      },
      bem_id: {
        id: "bem_id",
        label: "Bem",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Selecione o bem",
        helpText: "Bens sugeridos baseados no valor da parcela",
        icon: "Package"
      },
      valorParcela: {
        id: "valorParcela",
        label: "Valor da Parcela",
        type: "text",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "R$ 0,00",
        validation: {
          pattern: "^R\\$ [0-9]{1,3}(\\.[0-9]{3})*(,[0-9]{2})?$",
          custom: "currency_validation"
        },
        helpText: "Valor obtido do bem ou preenchido manualmente",
        icon: "DollarSign"
      },
      contrato_numero: {
        id: "contrato_numero",
        label: "Número do Contrato",
        type: "text",
        required: false,
        visible: true,
        priority: "medium",
        placeholder: "Ex: CONT_2024_001",
        helpText: "Número único do contrato (gerado automaticamente se vazio)",
        icon: "FileText"
      },
      metodoPagamento: {
        id: "metodoPagamento",
        label: "Método de Pagamento",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        options: [
          { value: "PIX", label: "PIX" },
          { value: "BOLETO", label: "Boleto" },
          { value: "CARTAO", label: "Cartão de Crédito" }
        ]
      }
    },
    quickActions: [
      {
        label: "Salvar Rascunho",
        action: "save_draft",
        icon: "FileText"
      },
    ],
    validation: {
      strict: false,
      showWarnings: true,
      contextualValidation: true
    }
  },

  LIDER_VENDAS: {
    title: "Novo Contrato - Líder de Vendas",
    description: "Cadastro completo com análise comparativa",
    icon: "Crown",
    steps: [
      {
        id: "cliente",
        title: "Cliente",
        description: "Selecione ou cadastre o cliente",
        icon: "User",
        fields: ["cliente_search", "cliente_id"],
        required: true
      },
      {
        id: "produto",
        title: "Produto",
        description: "Escolha o grupo e bem",
        icon: "Package",
        fields: ["grupo_id", "bem_id", "valorParcela", "meses_restantes", "valor_entrada", "contrato_numero"],
        required: true
      },
      {
        id: "financeiro",
        title: "Valores",
        description: "Configure valores e pagamento",
        icon: "DollarSign",
        fields: ["metodoPagamento", "consultor_id", "observacoes"],
        required: true
      }
    ],
    fields: {
      cliente_search: {
        id: "cliente_search",
        label: "Buscar Cliente",
        type: "search",
        required: false, // Campo de busca não deve ser obrigatório para validação
        visible: true,
        priority: "high",
        placeholder: "Digite CPF, nome ou email",
        helpText: "Busque por CPF, nome completo ou email",
        icon: "User"
      },
      cliente_id: {
        id: "cliente_id",
        label: "Cliente",
        type: "select",
        required: true,
        visible: false, // Hidden, populated by search
        priority: "high"
      },
      grupo_id: {
        id: "grupo_id",
        label: "Grupo",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Selecione o grupo",
        helpText: "Grupos sugeridos baseados no perfil do cliente",
        icon: "Package"
      },
      bem_id: {
        id: "bem_id",
        label: "Bem",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Selecione o bem",
        helpText: "Bens sugeridos baseados no valor da parcela",
        icon: "Package"
      },
      valorParcela: {
        id: "valorParcela",
        label: "Valor da Parcela",
        type: "text",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "R$ 0,00",
        validation: {
          pattern: "^R\\$ [0-9]{1,3}(\\.[0-9]{3})*(,[0-9]{2})?$",
          custom: "currency_validation"
        },
        helpText: "Valor obtido do bem ou preenchido manualmente",
        icon: "DollarSign"
      },
      meses_restantes: {
        id: "meses_restantes",
        label: "Meses Restantes",
        type: "number",
        required: false,
        visible: true,
        priority: "medium",
        placeholder: "Ex: 150",
        helpText: "Ajuste a quantidade de meses restantes do grupo",
        icon: "Calendar"
      },
      contrato_numero: {
        id: "contrato_numero",
        label: "Número do Contrato",
        type: "text",
        required: false,
        visible: true,
        priority: "medium",
        placeholder: "Ex: CONT_2024_001",
        helpText: "Número único do contrato (gerado automaticamente se vazio)",
        icon: "FileText"
      },
      valor_entrada: {
        id: "valor_entrada",
        label: "Valor da Entrada",
        type: "text",
        required: false,
        visible: true,
        priority: "medium",
        placeholder: "R$ 0,00",
        validation: {
          pattern: "^R\\$ [0-9]{1,3}(\\.[0-9]{3})*(,[0-9]{2})?$",
          custom: "currency_validation"
        },
        helpText: "Valor da entrada inicial do contrato",
        icon: "DollarSign"
      },
      metodoPagamento: {
        id: "metodoPagamento",
        label: "Método de Pagamento",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        options: [
          { value: "PIX", label: "PIX" },
          { value: "BOLETO", label: "Boleto" },
          { value: "CARTAO", label: "Cartão de Crédito" }
        ]
      }
    },
    quickActions: [
      {
        label: "Salvar Rascunho",
        action: "save_draft",
        icon: "FileText"
      },
    ],
    validation: {
      strict: false,
      showWarnings: true,
      contextualValidation: true
    }
  },

  USER: {
    title: "Novo Contrato",
    description: "Formulário padrão",
    icon: "FileText",
    steps: [
      {
        id: "cliente",
        title: "Cliente",
        description: "Selecione ou cadastre o cliente",
        icon: "User",
        fields: ["cliente_search", "cliente_id"],
        required: true
      },
      {
        id: "produto",
        title: "Produto",
        description: "Escolha o grupo e bem",
        icon: "Package",
        fields: ["grupo_id", "bem_id", "valorParcela", "contrato_numero"],
        required: true
      },
      {
        id: "financeiro",
        title: "Valores",
        description: "Configure valores e pagamento",
        icon: "DollarSign",
        fields: ["metodoPagamento"],
        required: true
      }
    ],
    fields: {
      cliente_search: {
        id: "cliente_search",
        label: "Buscar Cliente",
        type: "search",
        required: false,
        visible: true,
        priority: "high",
        placeholder: "Digite CPF, nome ou email",
        helpText: "Busque por CPF, nome completo ou email",
        icon: "User"
      },
      cliente_id: {
        id: "cliente_id",
        label: "Cliente",
        type: "select",
        required: true,
        visible: false,
        priority: "high"
      },
      grupo_id: {
        id: "grupo_id",
        label: "Grupo",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Selecione o grupo",
        helpText: "Grupos sugeridos baseados no perfil do cliente",
        icon: "Package"
      },
      bem_id: {
        id: "bem_id",
        label: "Bem",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "Selecione o bem",
        helpText: "Bens sugeridos baseados no valor da parcela",
        icon: "Package"
      },
      valorParcela: {
        id: "valorParcela",
        label: "Valor da Parcela",
        type: "text",
        required: true,
        visible: true,
        priority: "high",
        placeholder: "R$ 0,00",
        validation: {
          pattern: "^R\\$ [0-9]{1,3}(\\.[0-9]{3})*(,[0-9]{2})?$",
          custom: "currency_validation"
        },
        helpText: "Valor obtido do bem ou preenchido manualmente",
        icon: "DollarSign"
      },
      contrato_numero: {
        id: "contrato_numero",
        label: "Número do Contrato",
        type: "text",
        required: false,
        visible: true,
        priority: "medium",
        placeholder: "Ex: CONT_2024_001",
        helpText: "Número único do contrato (gerado automaticamente se vazio)",
        icon: "FileText"
      },
      metodoPagamento: {
        id: "metodoPagamento",
        label: "Método de Pagamento",
        type: "select",
        required: true,
        visible: true,
        priority: "high",
        options: [
          { value: "PIX", label: "PIX" },
          { value: "BOLETO", label: "Boleto" },
          { value: "CARTAO", label: "Cartão de Crédito" }
        ]
      }
    },
    quickActions: [
      {
        label: "Salvar Rascunho",
        action: "save_draft",
        icon: "FileText"
      },
    ],
    validation: {
      strict: false,
      showWarnings: true,
      contextualValidation: true
    }
  },
  ASSOCIADO: {
    title: "Novo Contrato - Associado",
    description: "Acesso completo a todas as funcionalidades",
    icon: "Settings",
    steps: [
      {
        id: "cliente",
        title: "Cliente",
        description: "Dados completos do cliente",
        icon: "User",
        fields: ["cliente_search", "cliente_id"],
        required: true
      },
      {
        id: "produto",
        title: "Produto",
        description: "Grupo, bem e valores detalhados",
        icon: "Package",
        fields: ["grupo_id", "bem_id", "valorParcela", "taxa_adm", "valor_total", "valor_entrada", "contrato_numero"],
        required: true
      },
      {
        id: "financeiro",
        title: "Financeiro",
        description: "Valores, comissões e pagamentos",
        icon: "DollarSign",
        fields: ["metodoPagamento", "comissao_vendedor", "comissao_consultor", "observacoes"],
        required: true
      }
    ],
    fields: {},
    quickActions: [],
    validation: {
      strict: true,
      showWarnings: true,
      contextualValidation: true
    }
  },
  POS_VENDA: {
    title: "Novo Contrato - Pós Venda",
    description: "Configurações de pós-venda",
    icon: "Settings",
    steps: [],
    fields: {},
    quickActions: [],
    validation: {
      strict: true,
      showWarnings: true,
      contextualValidation: true
    }
  }
};

// Pós-processamento para herdar campos e evitar duplicação massiva
if (formConfigs.RELACIONADOR) {
  formConfigs.RELACIONADOR.fields = { ...formConfigs.VENDEDOR.fields, ...formConfigs.RELACIONADOR.fields };
}
if (formConfigs.LIDER_VENDAS) {
  formConfigs.LIDER_VENDAS.fields = { ...formConfigs.SECRETARIA.fields, ...formConfigs.LIDER_VENDAS.fields };
}
if (formConfigs.SUPER_ADMIN && formConfigs.ADMIN) {
  formConfigs.SUPER_ADMIN.fields = { ...formConfigs.ADMIN.fields };
  formConfigs.SUPER_ADMIN.steps = [...formConfigs.ADMIN.steps];
  formConfigs.SUPER_ADMIN.quickActions = [...formConfigs.ADMIN.quickActions];
}
if (formConfigs.ADMIN) {
  formConfigs.ADMIN.fields = { ...formConfigs.SECRETARIA.fields, ...formConfigs.ADMIN.fields };
}
if (formConfigs.SUPERVISOR) {
  formConfigs.SUPERVISOR.fields = { ...formConfigs.SECRETARIA.fields, ...formConfigs.SUPERVISOR.fields };
}
if (formConfigs.ASSOCIADO && formConfigs.ADMIN) {
  formConfigs.ASSOCIADO.fields = { ...formConfigs.ADMIN.fields };
  formConfigs.ASSOCIADO.steps = [...formConfigs.ADMIN.steps];
  formConfigs.ASSOCIADO.quickActions = [...formConfigs.ADMIN.quickActions];
}
if (formConfigs.POS_VENDA && formConfigs.SECRETARIA) {
  formConfigs.POS_VENDA.fields = { ...formConfigs.SECRETARIA.fields };
  formConfigs.POS_VENDA.steps = [...formConfigs.SECRETARIA.steps];
}
